import { useState, useCallback, useRef, useEffect } from 'react';
import {
  Box, Button, Dialog, DialogActions, DialogContent,
  Slider, ToggleButton, ToggleButtonGroup, IconButton, Tooltip, Typography,
  Switch, FormControlLabel, Chip, Divider, CircularProgress,
} from '@mui/material';
import {
  RotateLeft, RotateRight, GridOn, GridOff,
  ZoomIn, ZoomOut, RestartAlt, CropFree, CenterFocusStrong,
} from '@mui/icons-material';
import { alpha, useTheme } from '@mui/material/styles';
import { Cropper } from 'react-advanced-cropper';
import type { CropperRef } from 'react-advanced-cropper';
import 'react-advanced-cropper/dist/style.css';
import { ASPECT_PRESETS, type AspectPreset } from '../../../types/photo';

interface CropDialogProps {
  open: boolean;
  imageUrl: string;
  fileName: string;
  initialFullSelection?: boolean; // Start with crop covering the full image
  /** Preselect the preset entry matching this W:H ratio (e.g. the Rock
   *  TCS/UCS 3:5 report crop, BUG-2026-0137 §11). Default: preset 0. */
  defaultAspect?: number;
  /** Preset list override — Rock TCS/UCS/UCS-NS pass
   *  ROCK_REPORT_ASPECT_PRESETS ("Report (3:5)" only report ratio,
   *  BUG-2026-0137/0138 rework). Default: ASPECT_PRESETS. */
  presets?: readonly AspectPreset[];
  onConfirm: (croppedFile: File) => void;
  onCancel: () => void;
}

// REQ-2026-0009 Q9.6: zoom is clamped to 0.5×–4× of the auto-crop bbox.
// react-advanced-cropper's zoomImage(factor) is multiplicative, so we track
// the cumulative zoom ourselves and refuse out-of-range clicks (also caps the
// wheel and slider).
const MIN_ZOOM = 0.5;
const MAX_ZOOM = 4;
const ZOOM_STEP = 1.1; // ~10 % per click — feels right with mouse + trackpad.

/**
 * BUG-2026-0158 feedback: originals are several MB and blob storage is slow,
 * so the dialog used to open onto a black void for a few seconds. The image
 * is now STREAMED down with fetch so a real percentage can be shown (the
 * storage account's CORS rules expose Content-Length), then handed to the
 * cropper as a local object URL. Any fetch/CORS failure falls back to letting
 * the cropper load the remote URL itself — spinner without a %, hidden once
 * the image is ready.
 */
interface ImageLoadState {
  url: string | null;
  pct: number | null; // null = size unknown / fallback (indeterminate spinner)
  loadedBytes: number;
  totalBytes: number | null;
}

const mb = (bytes: number) => (bytes / 1048576).toFixed(1);

export const CropDialog = ({ open, imageUrl, fileName, initialFullSelection, defaultAspect, presets = ASPECT_PRESETS, onConfirm, onCancel }: CropDialogProps) => {
  const theme = useTheme();
  const cropperRef = useRef<CropperRef>(null);
  const [aspectIdx, setAspectIdx] = useState(() => {
    if (defaultAspect === undefined) return 0;
    const idx = presets.findIndex((p) => p.value !== undefined && Math.abs(p.value - defaultAspect) < 1e-6);
    return idx >= 0 ? idx : 0;
  });
  const [rotation, setRotation] = useState(0);
  const [showGrid, setShowGrid] = useState(true);
  const [processing, setProcessing] = useState(false);
  const [cropInfo, setCropInfo] = useState({ cropW: 0, cropH: 0, imgW: 0, imgH: 0 });
  const [zoom, setZoom] = useState(1); // REQ-2026-0009: cumulative zoom (1 = Fit / auto-crop bbox)
  const [imgState, setImgState] = useState<ImageLoadState>({ url: null, pct: null, loadedBytes: 0, totalBytes: null });
  const [cropperReady, setCropperReady] = useState(false);

  const aspect = presets[aspectIdx]?.value;

  // Streamed pre-download of the original with % progress (see ImageLoadState).
  useEffect(() => {
    if (!open || !imageUrl) return undefined;
    // Local object/data URLs (fresh file uploads) resolve instantly — no need
    // for the streaming pass.
    if (imageUrl.startsWith('blob:') || imageUrl.startsWith('data:')) {
      setImgState({ url: imageUrl, pct: 100, loadedBytes: 0, totalBytes: null });
      return undefined;
    }
    let cancelled = false;
    let objectUrl: string | null = null;
    const controller = new AbortController();
    setCropperReady(false);
    setImgState({ url: null, pct: null, loadedBytes: 0, totalBytes: null });
    (async () => {
      try {
        const resp = await fetch(imageUrl, { signal: controller.signal });
        if (!resp.ok || !resp.body) throw new Error(`HTTP ${resp.status}`);
        const totalBytes = Number(resp.headers.get('Content-Length')) || null;
        const reader = resp.body.getReader();
        const chunks: BlobPart[] = [];
        let loadedBytes = 0;
        for (;;) {
          const { done, value } = await reader.read();
          if (done) break;
          chunks.push(value);
          loadedBytes += value.byteLength;
          if (!cancelled) {
            setImgState({
              url: null,
              // hold at 99 until the object URL is actually ready
              pct: totalBytes ? Math.min(99, Math.round((loadedBytes / totalBytes) * 100)) : null,
              loadedBytes,
              totalBytes,
            });
          }
        }
        objectUrl = URL.createObjectURL(
          new Blob(chunks, { type: resp.headers.get('Content-Type') ?? 'image/jpeg' }),
        );
        if (cancelled) {
          URL.revokeObjectURL(objectUrl);
          return;
        }
        setImgState({ url: objectUrl, pct: 100, loadedBytes, totalBytes });
      } catch {
        // CORS/network failure — let the cropper stream the remote URL itself.
        if (!cancelled) setImgState({ url: imageUrl, pct: null, loadedBytes: 0, totalBytes: null });
      }
    })();
    return () => {
      cancelled = true;
      controller.abort();
      if (objectUrl) URL.revokeObjectURL(objectUrl);
    };
  }, [open, imageUrl]);

  const handleCropChange = useCallback(() => {
    const cropper = cropperRef.current;
    if (!cropper) return;
    const coords = cropper.getCoordinates();
    const img = cropper.getImage();
    if (coords && img) {
      setCropInfo({
        cropW: Math.round(coords.width),
        cropH: Math.round(coords.height),
        imgW: Math.round(img.width),
        imgH: Math.round(img.height),
      });
    }
  }, []);

  const handleConfirm = useCallback(async () => {
    const cropper = cropperRef.current;
    if (!cropper) return;
    setProcessing(true);
    try {
      const canvas = cropper.getCanvas();
      if (!canvas) throw new Error('Failed to get canvas');
      const blob = await new Promise<Blob>((resolve, reject) => {
        canvas.toBlob(
          (b) => (b ? resolve(b) : reject(new Error('toBlob failed'))),
          fileName.toLowerCase().endsWith('.png') ? 'image/png' : 'image/jpeg',
          0.92,
        );
      });
      const file = new File([blob], fileName, { type: blob.type });
      onConfirm(file);
    } catch (err) {
      console.error('Crop failed:', err);
    } finally {
      setProcessing(false);
    }
  }, [fileName, onConfirm]);

  const rotate = (deg: number) => {
    setRotation((r) => {
      const next = r + deg;
      cropperRef.current?.rotateImage(deg);
      return next;
    });
  };

  /**
   * REQ-2026-0009 Q9.6: clamped zoom. We only apply the multiplicative step
   * if the resulting cumulative zoom stays inside [MIN_ZOOM, MAX_ZOOM].
   */
  const applyZoom = (factor: number) => {
    const target = zoom * factor;
    const clamped = Math.max(MIN_ZOOM, Math.min(MAX_ZOOM, target));
    if (clamped === zoom) return; // already at the boundary
    const effective = clamped / zoom;
    cropperRef.current?.zoomImage(effective);
    setZoom(clamped);
  };

  /**
   * REQ-2026-0009 Q9.6: "Fit" returns the view to 1× — exactly the auto-crop
   * bbox the backend proposed.
   */
  const fitToBbox = () => {
    // Reset stencil to the default size the cropper computes from the image,
    // which lines up with the auto-crop bbox (or full image in manual mode).
    cropperRef.current?.reset();
    setZoom(1);
    setRotation(0);
  };

  const resetAll = () => {
    setRotation(0);
    setAspectIdx(0);
    setZoom(1);
    cropperRef.current?.reset();
  };

  return (
    <Dialog open={open} onClose={onCancel} maxWidth={false} fullScreen
      PaperProps={{ sx: { bgcolor: '#111', color: '#fff' } }}>
      {/* Top bar */}
      <Box sx={{
        display: 'flex', alignItems: 'center', justifyContent: 'space-between',
        px: 2.5, py: 1, bgcolor: '#1a1a1a', borderBottom: '1px solid #333',
        flexWrap: 'wrap', gap: 1,
      }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <CropFree sx={{ color: theme.palette.primary.main }} />
          <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>Crop Photo</Typography>
          <Divider orientation="vertical" flexItem sx={{ borderColor: '#444' }} />
          <ToggleButtonGroup
            size="small" exclusive value={aspectIdx}
            onChange={(_, v) => v !== null && setAspectIdx(v)}
            sx={{
              '& .MuiToggleButton-root': {
                px: 1.5, py: 0.4, fontSize: '0.72rem', fontWeight: 600,
                color: '#aaa', borderColor: '#444',
                '&.Mui-selected': { bgcolor: alpha(theme.palette.primary.main, 0.2), color: '#fff', borderColor: theme.palette.primary.main },
              },
            }}
          >
            {presets.map((p, i) => (
              <ToggleButton key={p.label} value={i}>{p.label}</ToggleButton>
            ))}
          </ToggleButtonGroup>
        </Box>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Chip size="small" label={`Crop: ${cropInfo.cropW} × ${cropInfo.cropH}`}
            sx={{ bgcolor: '#333', color: '#ccc', fontSize: '0.7rem', fontFamily: 'monospace' }} />
          <Chip size="small" label={`Image: ${cropInfo.imgW} × ${cropInfo.imgH}`}
            sx={{ bgcolor: '#333', color: '#888', fontSize: '0.7rem', fontFamily: 'monospace' }} />
          {/* REQ-2026-0009: surface the current zoom factor (relative to auto-crop bbox). */}
          <Chip size="small" label={`Zoom: ${zoom.toFixed(2)}×`}
            sx={{ bgcolor: '#333', color: '#88aaff', fontSize: '0.7rem', fontFamily: 'monospace' }} />
        </Box>
      </Box>

      {/* Cropper area */}
      <DialogContent sx={{ p: 0, position: 'relative', flex: 1, overflow: 'hidden', bgcolor: '#111' }}>
        <Box sx={{
          width: '100%', height: '100%',
          '& .advanced-cropper': { height: '100%' },
          // Force grid visibility via CSS class override
          '& .advanced-cropper-stencil-grid': {
            opacity: showGrid ? '1 !important' : '0 !important',
          },
        }}>
          {imgState.url && (
            <Cropper
              ref={cropperRef}
              src={imgState.url}
              stencilProps={{
                aspectRatio: aspect ? { minimum: aspect, maximum: aspect } : undefined,
                movable: true,
                resizable: true,
                lines: true,
                handlers: true,
                grid: true,
              }}
              transformImage={{
                adjustStencil: false,
                wheel: { ratio: 0.015 },
              } as any}
              defaultSize={initialFullSelection ? ({ imageSize }) => ({
                width: imageSize.width,
                height: imageSize.height,
              }) : undefined}
              onReady={() => setCropperReady(true)}
              onChange={handleCropChange}
              backgroundClassName="cropper-bg"
              style={{ height: '100%', width: '100%' }}
            />
          )}
        </Box>
        {/* Download/decode progress overlay — real % while the original streams
            from blob storage, indeterminate on the direct-URL fallback. */}
        {(!imgState.url || !cropperReady) && (
          <Box sx={{
            position: 'absolute', inset: 0, display: 'flex', flexDirection: 'column',
            alignItems: 'center', justifyContent: 'center', gap: 2, bgcolor: '#111',
          }}>
            <Box sx={{ position: 'relative', display: 'inline-flex' }}>
              <CircularProgress
                size={72} thickness={3.2}
                variant={imgState.pct !== null ? 'determinate' : 'indeterminate'}
                value={imgState.pct ?? 0}
                sx={{ color: theme.palette.primary.main }}
              />
              {imgState.pct !== null && (
                <Box sx={{ position: 'absolute', inset: 0, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                  <Typography variant="caption" sx={{ color: '#fff', fontWeight: 700, fontSize: '0.95rem' }}>
                    {imgState.pct}%
                  </Typography>
                </Box>
              )}
            </Box>
            <Typography variant="body2" sx={{ color: '#bbb' }}>
              {imgState.url ? 'Preparing image…' : 'Loading original photo…'}
            </Typography>
            {imgState.totalBytes !== null && !imgState.url && (
              <Typography variant="caption" sx={{ color: '#666', fontFamily: 'monospace' }}>
                {mb(imgState.loadedBytes)} / {mb(imgState.totalBytes)} MB
              </Typography>
            )}
          </Box>
        )}
      </DialogContent>

      {/* Bottom toolbar */}
      <DialogActions sx={{
        px: 2.5, py: 1.5, bgcolor: '#1a1a1a', borderTop: '1px solid #333',
        justifyContent: 'space-between', flexWrap: 'wrap', gap: 1,
      }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, flexWrap: 'wrap' }}>
          {/* Rotation: fine ±1° and coarse ±90° */}
          <Tooltip title="Rotate -90°">
            <IconButton size="small" sx={{ color: '#aaa' }} onClick={() => rotate(-90)}><RotateLeft /></IconButton>
          </Tooltip>
          <Tooltip title="Rotate -1°">
            <Button size="small" sx={{ color: '#aaa', minWidth: 28, fontSize: '0.7rem' }} onClick={() => rotate(-1)}>-1°</Button>
          </Tooltip>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mx: 0.5 }}>
            <Typography variant="caption" sx={{ color: '#888', minWidth: 72 }}>Rotate {rotation}°</Typography>
            <Slider size="small" value={rotation} min={-180} max={180} step={0.1}
              onChange={(_, v) => {
                const diff = (v as number) - rotation;
                cropperRef.current?.rotateImage(diff);
                setRotation(v as number);
              }}
              sx={{ width: 120, color: theme.palette.primary.main, '& .MuiSlider-thumb': { width: 14, height: 14 } }} />
          </Box>
          <Tooltip title="Rotate +1°">
            <Button size="small" sx={{ color: '#aaa', minWidth: 28, fontSize: '0.7rem' }} onClick={() => rotate(1)}>+1°</Button>
          </Tooltip>
          <Tooltip title="Rotate +90°">
            <IconButton size="small" sx={{ color: '#aaa' }} onClick={() => rotate(90)}><RotateRight /></IconButton>
          </Tooltip>

          <Divider orientation="vertical" flexItem sx={{ borderColor: '#444', mx: 1 }} />

          {/* REQ-2026-0009 Q9.6: clamped zoom (0.5×–4×) + Fit-to-bbox button. */}
          <Tooltip title={`Zoom out (min ${MIN_ZOOM}×)`}>
            <span>
              <IconButton size="small" sx={{ color: '#aaa' }}
                disabled={zoom <= MIN_ZOOM}
                onClick={() => applyZoom(1 / ZOOM_STEP)}>
                <ZoomOut />
              </IconButton>
            </span>
          </Tooltip>
          <Tooltip title={`Zoom in (max ${MAX_ZOOM}×)`}>
            <span>
              <IconButton size="small" sx={{ color: '#aaa' }}
                disabled={zoom >= MAX_ZOOM}
                onClick={() => applyZoom(ZOOM_STEP)}>
                <ZoomIn />
              </IconButton>
            </span>
          </Tooltip>
          <Tooltip title="Fit to auto-crop bounding box (1×)">
            <Button size="small" startIcon={<CenterFocusStrong sx={{ fontSize: 16 }} />}
              onClick={fitToBbox}
              sx={{ color: '#aaa', textTransform: 'none', fontSize: '0.7rem', '&:hover': { color: '#fff' } }}>
              Fit
            </Button>
          </Tooltip>

          <Divider orientation="vertical" flexItem sx={{ borderColor: '#444', mx: 1 }} />

          {/* Grid toggle */}
          <FormControlLabel
            control={<Switch size="small" checked={showGrid} onChange={(_, v) => setShowGrid(v)}
              sx={{ '& .MuiSwitch-thumb': { width: 14, height: 14 }, '& .MuiSwitch-switchBase': { p: '6px' } }} />}
            label={<Box sx={{ display: 'flex', alignItems: 'center', gap: 0.3 }}>
              {showGrid ? <GridOn sx={{ fontSize: 16, color: '#aaa' }} /> : <GridOff sx={{ fontSize: 16, color: '#555' }} />}
              <Typography variant="caption" sx={{ color: '#888' }}>Grid</Typography>
            </Box>}
            sx={{ mx: 0 }}
          />

          <Divider orientation="vertical" flexItem sx={{ borderColor: '#444', mx: 1 }} />

          {/* Reset */}
          <Button size="small" startIcon={<RestartAlt />} onClick={resetAll}
            sx={{ color: '#aaa', textTransform: 'none', fontSize: '0.75rem', '&:hover': { color: '#fff' } }}>
            Reset
          </Button>
        </Box>

        <Box sx={{ display: 'flex', gap: 1.5, alignItems: 'center' }}>
          {/* REQ-2026-0009 Q9.5: explicit Discard + Confirm (also kept "Crop & Upload" copy for users who reach this via the edit-crop path). */}
          <Button onClick={onCancel} sx={{ textTransform: 'none', color: '#aaa', '&:hover': { color: '#fff' } }}>
            Discard
          </Button>
          <Button variant="contained" onClick={handleConfirm} disabled={processing}
            sx={{
              textTransform: 'none', fontWeight: 600, px: 3,
              bgcolor: theme.palette.primary.main,
              '&:hover': { bgcolor: theme.palette.primary.dark },
            }}>
            {processing ? 'Processing...' : 'Confirm crop'}
          </Button>
        </Box>
      </DialogActions>
    </Dialog>
  );
};
